import React from "react";
import {
    Text, Box, IconButton, Tooltip, Spinner, Stack, HStack,
    ButtonGroup, Button, Select, Icon
} from "@chakra-ui/react";
import { MdRefresh, MdInfoOutline, MdShowChart, MdChevronLeft, MdChevronRight, MdEvent, MdContacts } from "react-icons/md";
import SvgLineChart from "../horizon/views/dashboard/shared/SvgLineChart";
import { HeaderBox } from "../stats/sharedCardStyles";
import { DATA_TYPES, CHART_LABELS } from "./feeChartConfig";
import { useAdminPalette } from "./adminPalette";

const TYPE_OPTIONS = [
    { value: DATA_TYPES.RESERVATIONS, label: "Réservations", icon: MdEvent },
    { value: DATA_TYPES.CONTACTS, label: "Contacts", icon: MdContacts },
    { value: DATA_TYPES.TOTAL, label: "Total*", icon: null }
];

/**
 * Warning note shown when the mixed Total view is active. The Total aggregates
 * reservation commission and gross contact fees, which are two different natures.
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function MixedTotalNote({ isMobile }) {
    const palette = useAdminPalette();
    return (
        <Box display="flex" alignItems="center" gap={2} mb={4} p={isMobile ? "8px" : "10px"}
            background="rgba(255, 181, 71, 0.12)" borderRadius="10px" border="1px solid rgba(255, 181, 71, 0.35)">
            <Icon as={MdInfoOutline} color="orange.500" boxSize={isMobile ? "16px" : "18px"} />
            <Text color={palette.textSecondary} fontSize={isMobile ? "0.68rem" : "0.72rem"}>
                * Le Total agrège la commission des réservations et les frais de contact bruts (deux natures différentes).
            </Text>
        </Box>
    );
}

/**
 * Renders the header title area with icon and year subtitle.
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {number} props.currentYear - Currently displayed year
 */
function FeeHeaderTitle({ isMobile, currentYear }) {
    const palette = useAdminPalette();
    return (
        <HStack spacing={3} alignItems="center">
            <Box display="flex" alignItems="center" justifyContent="center"
                w={isMobile ? "34px" : "38px"} h={isMobile ? "34px" : "38px"} borderRadius="10px"
                background={palette.accentTint}>
                <Icon as={MdShowChart} color={palette.accent} boxSize={isMobile ? "18px" : "20px"} />
            </Box>
            <Box>
                <Text color={palette.textPrimary} fontWeight={700} letterSpacing="0.2px"
                    fontSize={isMobile ? "0.9rem" : "1rem"} lineHeight={1.2}>
                    Diagramme des revenus
                </Text>
                <Text color={palette.textMuted} fontSize={isMobile ? "0.7rem" : "0.78rem"} fontWeight={500}>
                    Année {currentYear}
                </Text>
            </Box>
        </HStack>
    );
}

/**
 * Card header with title and refresh button.
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {number} props.currentYear - Currently displayed year
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onRefresh - Refresh handler
 */
function FeeHeader({ isMobile, currentYear, loading, onRefresh }) {
    const palette = useAdminPalette();
    return (
        <HeaderBox mb={isMobile ? 3 : 4}>
            <FeeHeaderTitle isMobile={isMobile} currentYear={currentYear} />
            <HStack spacing={1}>
                <Tooltip label="Revenus mensuels de l'année en cours" hasArrow placement="top">
                    <IconButton aria-label="Info" size="sm" variant="ghost" minW="auto" h="auto"
                        p={isMobile ? "4px" : "6px"} color={palette.textMuted}
                        icon={<Icon as={MdInfoOutline} boxSize={isMobile ? "16px" : "18px"} />} />
                </Tooltip>
                <Tooltip label="Actualiser" hasArrow placement="top">
                    <IconButton aria-label="Actualiser" size="sm" variant="ghost" onClick={onRefresh} isDisabled={loading}
                        minW="auto" h="auto" p={isMobile ? "4px" : "6px"} color={palette.textSecondary}
                        icon={loading
                            ? <Spinner size="xs" color={palette.accent} />
                            : <Icon as={MdRefresh} boxSize={isMobile ? "16px" : "18px"} />} />
                </Tooltip>
            </HStack>
        </HeaderBox>
    );
}

/**
 * Single toggle button used inside the data-type selector group.
 * @param {Object} props - Component props
 * @param {Object} props.option - Option descriptor (value, label, icon)
 * @param {boolean} props.selected - Whether this option is active
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {Function} props.onClick - Click handler
 */
function TypeToggleButton({ option, selected, isMobile, onClick }) {
    const palette = useAdminPalette();
    return (
        <Button onClick={onClick} fontSize={isMobile ? "0.72rem" : "0.8rem"}
            px={isMobile ? "10px" : "14px"}
            leftIcon={option.icon ? <Icon as={option.icon} boxSize={isMobile ? "14px" : "16px"} /> : undefined}
            color={selected ? "white" : palette.textSecondary}
            borderColor={selected ? palette.accent : palette.border}
            background={selected ? palette.accent : "transparent"}
            _hover={{ background: selected ? palette.accent : palette.rowBg }}>
            {option.label}
        </Button>
    );
}

/**
 * Type toggle and currency dropdown selectors.
 * @param {Object} props - Component props
 * @param {string} props.selectedType - Selected data type
 * @param {Function} props.onTypeChange - Type change handler (receives the new value)
 * @param {string} props.selectedCurrency - Selected currency
 * @param {Function} props.onCurrencyChange - Currency change handler
 * @param {Array<string>} props.currencies - Available currencies
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function FeeSelectors({ selectedType, onTypeChange, selectedCurrency, onCurrencyChange, currencies, isMobile }) {
    const palette = useAdminPalette();
    return (
        <Stack spacing={4} mb={4} alignItems="center">
            <Box>
                <Text color={palette.textMuted} fontSize={isMobile ? "0.7rem" : "0.75rem"}
                    fontWeight={600} mb={2} textAlign="center">Type de revenu</Text>
                <ButtonGroup isAttached variant="outline" size="sm">
                    {TYPE_OPTIONS.map((opt) => (
                        <TypeToggleButton key={opt.value} option={opt} selected={selectedType === opt.value}
                            isMobile={isMobile} onClick={() => onTypeChange(opt.value)} />
                    ))}
                </ButtonGroup>
            </Box>
            <CurrencySelect selectedCurrency={selectedCurrency} onChange={onCurrencyChange} currencies={currencies} isMobile={isMobile} />
        </Stack>
    );
}

/**
 * Currency dropdown selector.
 * @param {Object} props - Component props
 * @param {string} props.selectedCurrency - Selected currency value
 * @param {Function} props.onChange - Change handler
 * @param {Array<string>} props.currencies - Available currency codes
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function CurrencySelect({ selectedCurrency, onChange, currencies, isMobile }) {
    const palette = useAdminPalette();
    return (
        <Box>
            <Text color={palette.textMuted} fontSize={isMobile ? "0.7rem" : "0.75rem"}
                fontWeight={600} mb={2} textAlign="center">Devise</Text>
            <Select value={selectedCurrency} onChange={onChange} size="sm"
                minW={isMobile ? "120px" : "150px"} color={palette.textPrimary} fontWeight={600}
                fontSize={isMobile ? "0.8rem" : "0.85rem"} borderColor={palette.border}>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
        </Box>
    );
}

/**
 * Chart container rendering the SVG revenue line with a loading overlay.
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Loading state
 * @param {{values: Array<number>, color: string}} props.series - Series values and stroke color
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function ChartContainer({ loading, series, isMobile }) {
    const palette = useAdminPalette();
    return (
        <Box w="100%" position="relative" mb={4} flexGrow={1}>
            {loading && (
                <Box position="absolute" inset="0" display="flex" justifyContent="center"
                    alignItems="center" zIndex={2}>
                    <Spinner color={palette.accent} />
                </Box>
            )}
            <SvgLineChart datasets={[{ label: "Revenus", values: series.values, color: series.color }]}
                labels={CHART_LABELS} height={isMobile ? "200px" : "260px"} />
        </Box>
    );
}

/**
 * Year total summary label.
 * @param {Object} props - Component props
 * @param {string} props.typeLabel - Localized type label
 * @param {number} props.year - Current year
 * @param {number} props.total - Total amount
 * @param {string} props.currency - Currency code
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function YearTotalLabel({ typeLabel, year, total, currency, isMobile }) {
    const palette = useAdminPalette();
    return (
        <Box display="flex" justifyContent="center" mb={4} p={isMobile ? "10px" : "12px"}
            background={palette.rowBg} borderRadius="10px">
            <Text color={palette.textPrimary} fontSize={isMobile ? "0.85rem" : "0.95rem"} fontWeight={700}>
                {typeLabel} {year}: {total.toLocaleString()} {currency}
            </Text>
        </Box>
    );
}

/**
 * Footer with year navigation arrows and refresh button.
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Loading state
 * @param {number} props.currentYear - Current year
 * @param {Function} props.onChangeYear - Year change handler
 * @param {Function} props.onRefresh - Refresh handler
 * @param {boolean} props.isMobile - Mobile viewport flag
 */
function FeeYearNavigation({ loading, currentYear, onChangeYear, onRefresh, isMobile }) {
    const palette = useAdminPalette();
    const navHover = { background: palette.rowBgHover, color: palette.accent };
    return (
        <Box mt="auto" p={isMobile ? "10px 16px" : "12px 20px"} display="flex"
            justifyContent="space-between" alignItems="center"
            background={palette.rowBg} borderRadius="12px" border="1px solid" borderColor={palette.border}>
            <Tooltip label="Année précédente" hasArrow placement="top">
                <IconButton aria-label="Année précédente" size="sm" variant="ghost" onClick={() => onChangeYear(-1)}
                    isDisabled={loading} minW="auto" h="auto" p={isMobile ? "6px" : "8px"}
                    color={palette.textSecondary} _hover={navHover}
                    icon={<Icon as={MdChevronLeft} boxSize={isMobile ? "20px" : "24px"} />} />
            </Tooltip>
            <Tooltip label="Actualiser" hasArrow placement="top">
                <IconButton aria-label="Actualiser" size="sm" variant="ghost" onClick={onRefresh} isDisabled={loading}
                    minW="auto" h="auto" p={isMobile ? "6px" : "8px"} color={palette.textSecondary} _hover={navHover}
                    icon={loading
                        ? <Spinner size="sm" color={palette.accent} />
                        : <Icon as={MdRefresh} boxSize={isMobile ? "18px" : "20px"} />} />
            </Tooltip>
            <Tooltip label="Année suivante" hasArrow placement="top">
                <IconButton aria-label="Année suivante" size="sm" variant="ghost" onClick={() => onChangeYear(1)}
                    isDisabled={loading || currentYear >= new Date().getFullYear()} minW="auto" h="auto"
                    p={isMobile ? "6px" : "8px"} color={palette.textSecondary} _hover={navHover}
                    _disabled={{ color: palette.textMuted, opacity: 0.4, cursor: "not-allowed" }}
                    icon={<Icon as={MdChevronRight} boxSize={isMobile ? "20px" : "24px"} />} />
            </Tooltip>
        </Box>
    );
}

export { FeeHeader, FeeSelectors, ChartContainer, YearTotalLabel, FeeYearNavigation, MixedTotalNote };
